import React from 'react';
import moment from 'moment';
import { History } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesHistoryTimeline,
  AgnesPageHeader,
  AgnesSkeleton,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  type AgnesHistoryTimelineItem,
} from '@agnes/ui';
import { useCboStore } from '@/stores/useCboStore';
import { confirm } from '@/stores/useConfirm';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import FormCboAnalise from './FormCboAnalise';

const dataQa = 'cbo-analise';

type LinhaHistorico = { [k: string]: any };

const Vazio = () => <span className="text-agnes-text-muted">—</span>;

const fmtQuando = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const m = moment(v, moment.ISO_8601);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : v;
};

/**
 * Tela de análise do CBO. Espelha o padrão de Rubricas (PageRubricasAnalise):
 * cabeçalho + abas Informações (edição da análise) e Histórico (reusa o
 * AgnesHistoryTimeline do design system).
 */
export default function PageTrabalhadorCboAnalise() {
  const { id } = useParams();
  const navigate = useNavigate();
  const trabalhadorId = Number(id);

  const historico = useCboStore((s) => s.historico);
  const carregarAnalise = useCboStore((s) => s.carregarAnalise);
  const carregarHistoricoAnalise = useCboStore((s) => s.carregarHistoricoAnalise);

  // Loading local da tela: cobre a análise + o histórico (que vem logo depois),
  // evitando o "flash" de estado vazio entre as duas chamadas. Regra do projeto:
  // skeleton no carregamento da tela.
  const [carregando, setCarregando] = React.useState(true);

  React.useEffect(() => {
    if (!Number.isFinite(trabalhadorId)) return;
    let ativo = true;
    setCarregando(true);
    carregarAnalise(trabalhadorId)
      .then(() => carregarHistoricoAnalise())
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [trabalhadorId, carregarAnalise, carregarHistoricoAnalise]);

  // Aba ativa (Informações | Histórico). Informações é o default (edição da análise mora
  // aqui); deep-link do Histórico mantido via ?tab=historico.
  // Sem permissão de escrita a tela é só consulta: a aba Informações (edição) não existe.
  const podeEditar = usePodeEditar(MENU_PERMISSIONS.CBO_EDITAR);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabEscolhida, setTab] = React.useState(
    searchParams.get('tab') === 'historico' ? 'historico' : 'informacoes',
  );
  const tab = podeEditar ? tabEscolhida : 'historico';

  // O formulário desmonta ao trocar de aba; sem o aviso, as edições se perderiam em silêncio.
  const [formSujo, setFormSujo] = React.useState(false);
  // O Radix dispara onValueChange no mousedown e de novo no foco; sem a trava o aviso abriria 2x.
  const confirmando = React.useRef(false);

  const mudarTab = async (v: string) => {
    if (tab === 'informacoes' && v !== 'informacoes' && formSujo) {
      if (confirmando.current) return;
      confirmando.current = true;
      try {
        const sair = await confirm({
          title: 'Alterações não salvas',
          message: 'Você tem alterações que não foram salvas e serão perdidas. Deseja sair?',
          okLabel: 'Sair',
          cancelLabel: 'Continuar editando',
        });
        if (!sair) return;
      } finally {
        confirmando.current = false;
      }
    }
    setFormSujo(false);
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'informacoes') next.delete('tab');
    else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  // Cada item do histórico já é uma alteração pronta (campo De → Para), vinda do backend.
  const itens: AgnesHistoryTimelineItem[] = historico.map((r: LinhaHistorico) => ({
    key: String(r.id ?? `${r.changedField}-${r.updatedAt}`),
    content: (
      <p className="text-sm text-agnes-text">
        <span className="font-semibold">
          {r.changedFieldDescriptiveName ?? r.changedField ?? 'Alteração'}:
        </span>{' '}
        {r.fromValue || <Vazio />} → {r.toValue || <Vazio />}
      </p>
    ),
    detail: [r.updatedBy, fmtQuando(r.updatedAt)].filter(Boolean).join(' · '),
  }));

  return (
    <div data-qa={dataQa}>
      <AgnesPageHeader
        icon={<History />}
        title="Análise do CBO"
        subtitle="Compare a ocupação do cliente com as recomendações Agnes"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'CBO', href: '/esocial/trabalhador/cbo' },
          { label: 'Análise do CBO' },
        ]}
      />

      <AgnesTabs className="mt-2 w-full" value={tab} onValueChange={mudarTab}>
        <AgnesTabsList>
          {podeEditar && (
            <AgnesTabsTrigger value="informacoes" data-qa={`${dataQa}-aba-informacoes`}>
              Informações
            </AgnesTabsTrigger>
          )}
          <AgnesTabsTrigger value="historico" data-qa={`${dataQa}-aba-historico`}>
            Histórico da análise
          </AgnesTabsTrigger>
        </AgnesTabsList>

        {podeEditar && (
          <AgnesTabsContent value="informacoes">
            <AgnesCard>
              <AgnesCardContent>
                <FormCboAnalise
                  onClose={() => navigate('/esocial/trabalhador/cbo')}
                  onDirtyChange={setFormSujo}
                />
              </AgnesCardContent>
            </AgnesCard>
          </AgnesTabsContent>
        )}

        <AgnesTabsContent value="historico">
          <AgnesCard>
            <AgnesCardContent>
              {carregando ? (
                <div data-qa={`${dataQa}-skeleton`} className="space-y-3 py-2">
                  <AgnesSkeleton className="h-5 w-1/3" />
                  <AgnesSkeleton className="h-5 w-2/3" />
                  <AgnesSkeleton className="h-5 w-1/2" />
                  <AgnesSkeleton className="h-5 w-3/5" />
                </div>
              ) : (
                <AgnesHistoryTimeline
                  items={itens}
                  dataQa="cbo-historico"
                  emptyTitle="Nenhuma alteração registrada"
                />
              )}
            </AgnesCardContent>
          </AgnesCard>
        </AgnesTabsContent>
      </AgnesTabs>
    </div>
  );
}
